import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiCall } from '../config/api';
import './JoinClass.css';

const JoinClass = ({ user }) => {
  const navigate = useNavigate();
  const [classCode, setClassCode] = useState('');
  const [isJoining, setIsJoining] = useState(false);
  const [currentClass, setCurrentClass] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }

    // Check if student is already in a class
    loadCurrentClass();
  }, [user, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCurrentClass = async () => {
    try {
      const response = await apiCall('/classes');
      
      if (!response.ok) {
        if (response.status === 401) {
          // User is not authenticated, redirect to auth
          navigate('/auth');
          return;
        }
        console.error('Failed to load classes:', response.status);
        return;
      }
      
      const data = await response.json();
      
      if (data.classes && data.classes.length > 0) {
        // Get the first class the student is enrolled in
        const studentClass = data.classes[0];
        const classData = {
          id: studentClass.id || studentClass._id,
          classCode: studentClass.classCode,
          teacherId: studentClass.teacher?.id || studentClass.teacher?._id,
          teacherName: `${studentClass.teacher?.firstName || ''} ${studentClass.teacher?.lastName || ''}`.trim(),
          className: studentClass.className,
          joinedDate: studentClass.createdAt
        };
        setCurrentClass(classData);
        
        // Store class data in localStorage for game progress tracking
        localStorage.setItem('aminole_current_class', JSON.stringify(classData));
      } else {
        // No classes found, clear any stored class data
        setCurrentClass(null);
        localStorage.removeItem('aminole_current_class');
      }
    } catch (error) {
      console.error('Error loading current class:', error);
      // Don't show error to user for this background operation
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (value.length <= 6) {
      setClassCode(value);
      setError('');
      setSuccess('');
    }
  };

  const joinClass = async () => {
    if (!classCode || classCode.length !== 6) {
      setError('Please enter a valid 6-character class code');
      return;
    }

    setIsJoining(true);
    setError('');
    setSuccess('');

    try {
      const response = await apiCall('/classes/join', {
        method: 'POST',
        body: JSON.stringify({ classCode })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSuccess(`Successfully joined ${data.className}!`);
        setClassCode('');
        setError('');
        
        // Store the joined class data immediately for game progress tracking
        const joinedClassData = {
          id: data.classId || data.class?.id || data.class?._id,
          classCode: data.classCode || data.class?.classCode,
          className: data.className || data.class?.className,
          teacherId: data.teacherId || data.class?.teacher?._id || data.class?.teacher?.id,
          teacherName: data.teacherName || `${data.class?.teacher?.firstName} ${data.class?.teacher?.lastName}`,
          joinedDate: new Date().toISOString()
        };
        localStorage.setItem('aminole_current_class', JSON.stringify(joinedClassData));
        
        // Refresh current class data
        setTimeout(() => {
          loadCurrentClass();
          setSuccess('');
        }, 2000);
      } else {
        // Handle different types of errors
        if (response.status === 401) {
          setError('You need to be logged in to join a class. Please log in and try again.');
          setTimeout(() => navigate('/auth'), 2000);
        } else if (response.status === 403) {
          setError('You do not have permission to join classes. Make sure you are logged in as a student.');
        } else if (response.status === 404) {
          setError('Class not found. Please check the class code and try again.');
        } else if (response.status === 400) {
          setError(data.error || data.errors?.[0]?.msg || 'Invalid class code or you are already enrolled in this class.');
        } else if (response.status === 500) {
          setError('Server error occurred. Please try again in a moment.');
        } else {
          setError(data.error || 'Failed to join class. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error joining class:', error);
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        setError('Network error. Please check your internet connection and try again.');
      } else {
        setError('An error occurred while joining the class. Please try again.');
      }
    } finally {
      setIsJoining(false);
    }
  };

  const leaveClass = async () => {
    if (window.confirm('Are you sure you want to leave this class? Your progress will still be saved, but you will need a new class code to rejoin.')) {
      try {
        const response = await apiCall('/classes/leave', {
          method: 'POST'
        });
        
        if (response.ok) {
          setCurrentClass(null);
          localStorage.removeItem('aminole_current_class');
          setSuccess('Successfully left the class.');
          setError('');
        } else {
          const data = await response.json().catch(() => ({}));
          setError(data.error || 'Failed to leave class. Please try again.');
        }
      } catch (error) {
        console.error('Error leaving class:', error);
        if (error.name === 'TypeError' && error.message.includes('fetch')) {
          setError('Network error. Please check your internet connection and try again.');
        } else {
          setError('An error occurred while leaving the class. Please try again.');
        }
      }
    }
  };

  return (
    <div className="join-class-page">
      <div className="join-class-header">
        <button className="back-button" onClick={() => navigate('/')}>
          ← Back to Game
        </button>
        <button className="refresh-button" onClick={loadCurrentClass}>
          🔄 Refresh
        </button>
        <h1>🎓 Join a Class</h1>
        <p>Enter a class code to join your teacher's class and track your progress!</p>
      </div>

      <div className="join-class-content">
        {currentClass ? (
          /* Already in a class */
          <div className="current-class-card">
            <h2>📚 Current Class</h2>
            <div className="class-info">
              <div className="class-details">
                <h3>{currentClass.teacherName}'s Class</h3>
                <p><strong>Class Code:</strong> {currentClass.classCode}</p>
                <p><strong>Joined:</strong> {new Date(currentClass.joinedDate).toLocaleDateString()}</p>
              </div>
              <div className="class-actions">
                <button className="leave-class-btn" onClick={leaveClass}>
                  Leave Class
                </button>
              </div>
            </div>
            <div className="class-benefits">
              <h4>✨ Benefits of being in a class:</h4>
              <ul>
                <li>Your teacher can track your progress</li>
                <li>Get personalized feedback on your performance</li>
                <li>Compete with classmates in a fun way</li>
                <li>Your game statistics are shared with your teacher</li>
              </ul>
            </div>
          </div>
        ) : (
          /* Not in a class - show join form */
          <div className="join-class-card">
            <h2>🔑 Enter Class Code</h2>
            
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}
            
            {success && (
              <div className="success-message">
                {success}
              </div>
            )}

            <div className="code-input-section">
              <label htmlFor="classCode">Class Code</label>
              <input
                type="text"
                id="classCode"
                value={classCode}
                onChange={handleInputChange}
                placeholder="Enter 6-character code"
                maxLength={6}
                className="class-code-input"
                disabled={isJoining}
              />
              <p className="input-help">
                Ask your teacher for the 6-character class code (e.g., ABC123)
              </p>
            </div>

            <button 
              className="join-btn"
              onClick={joinClass}
              disabled={isJoining || classCode.length !== 6}
            >
              {isJoining ? 'Joining...' : 'Join Class'}
            </button>

            <div className="join-benefits">
              <h4>🎯 Why join a class?</h4>
              <div className="benefits-grid">
                <div className="benefit-item">
                  <span className="benefit-icon">📈</span>
                  <span>Track Progress</span>
                </div>
                <div className="benefit-item">
                  <span className="benefit-icon">🏆</span>
                  <span>Get Recognition</span>
                </div>
                <div className="benefit-item">
                  <span className="benefit-icon">🤝</span>
                  <span>Teacher Support</span>
                </div>
                <div className="benefit-item">
                  <span className="benefit-icon">📊</span>
                  <span>Detailed Analytics</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JoinClass;
